import {
  TransactionStep,
  TransactionStepState,
  TxStepStatus,
  ValidationResult,
} from '@/common/types';
import { StoreType } from '@/store/mainStore';

// Step Management
/**
 * Adds a new step to the transaction flow
 * @param store - The global store instance
 * @param step - The step to add
 * @param position - Optional position to insert at (defaults to end)
 */
function addStep(store: StoreType, step: TransactionStep, position?: number): void {
  const currentSteps = store.txSteps;
  if (position !== undefined) {
    currentSteps.splice(position, 0, step);
  } else {
    currentSteps.push(step);
  }
  store.setTxSteps(currentSteps);
}

// Flow Control
/**
 * Executes a specific step in the transaction flow
 * Handles step execution, error handling, and progression to next step
 * @param store - The global store instance
 * @param stepIndex - Index of the step to execute
 */
async function executeStep(store: StoreType, stepIndex: number): Promise<void> {
  const currentStep = store.txSteps[stepIndex];
  if (!currentStep || !currentStep.execute || _isProcessing(store)) return;

  // Clear previous errors
  store.updateTxStepState(stepIndex, { error: undefined });
  try {
    // Execute the step
    await currentStep.execute(store, (updates: Partial<TransactionStepState>) =>
      store.updateTxStepState(stepIndex, updates),
    );
  } catch (error) {
    if (error instanceof Error) {
      store.updateTxStepState(stepIndex, {
        status: TxStepStatus.Failed,
        error: error.message,
      });
    }

    throw error;
  }

  // Success handling
  await currentStep.onSuccess?.();
  await moveToStep(store, stepIndex + 1);
}

/**
 * Moves the transaction flow to a specific step
 * Handles step validation, skipping, and status updates for all steps
 * @param store - The global store instance
 * @param targetStepIndex - Index of the step to move to
 */
async function moveToStep(store: StoreType, targetStepIndex: number): Promise<void> {
  if (targetStepIndex < 0 || targetStepIndex >= store.txSteps.length) return;
  // Validate current step and handle skipping
  const currentStep = store.txSteps[targetStepIndex];
  if (!currentStep) return;

  let result: ValidationResult | undefined = undefined;
  let error: string | undefined = undefined;
  let warning: string | undefined = undefined;
  if (currentStep.validateBefore) {
    result = await currentStep.validateBefore(store, (updates: Partial<TransactionStepState>) =>
      store.updateTxStepState(targetStepIndex, updates),
    );

    if (result?.skip) {
      console.log('skipping step', targetStepIndex);
      await moveToStep(store, targetStepIndex + 1);
      return;
    }
    error = result?.error;
    warning = result?.warning;
  }

  // Update step statuses based on current position
  store.txSteps.forEach((step, index) => {
    if (index < targetStepIndex) {
      store.updateTxStepState(index, {
        status: TxStepStatus.Confirmed,
        error: undefined,
        warning: undefined,
      });
    } else if (index === targetStepIndex) {
      store.updateTxStepState(index, {
        status: TxStepStatus.Pending,
        error,
        warning,
      });
    } else {
      store.updateTxStepState(index, {
        status: TxStepStatus.Unstarted,
        error: undefined,
        warning: undefined,
      });
    }
  });
}

/**
 * Restarts the entire transaction flow from the beginning
 * Resets all step states and moves to the first step
 * @param store - The global store instance
 */
function restartFlow(store: StoreType): void {
  // Reset allf steps
  store.txSteps.forEach((step, index) =>
    store.updateTxStepState(index, {
      status: TxStepStatus.Unstarted,
      error: undefined,
      warning: undefined,
    }),
  );
  moveToStep(store, 0);
}

function _isProcessing(store: StoreType): boolean {
  return store.txSteps.some((step) => step.state.status === TxStepStatus.Confirming);
}

export { moveToStep, addStep, executeStep, restartFlow };
